import React, { useState, useEffect, useRef } from 'react';
import { FormGroup } from 'ps-design';
import classnames from 'classnames';
import { func, array, number, string, bool } from 'prop-types';

import './BubbleInput.scss';

BubbleCopy.propTypes = {
  bubbles: array,
  setBubbles: func,
  dropdownOptions: array,
  lineBreakToHide: number,
  notFoundText: string,
  inputLabel: string,
  hideInputLabel: bool,
  showClearAll: bool,
  className: string,
  onFocus: func,
  onBlur: func,
  passFocus: bool,
  scrollIntoView: bool,
  scrollAlignToTop: bool
};

const emptyFunc = () => {};

BubbleCopy.defaultProps = {
  lineBreakToHide: 3,
  notFoundText: 'No options found',
  inputLabel: 'Label',
  hideInputLabel: false,
  showClearAll: true,
  scrollIntoView: true,
  scrollAlignToTop: false,
  onFocus: emptyFunc,
  onBlur: emptyFunc
};

const oneLineHeight = 35;

const KEY_CODES = {
  UP: 38,
  DOWN: 40,
  BACK: 8,
  ENTER: 13
};

export default function BubbleCopy(props) {
  const [height, setHeight] = useState(36);
  const bubbleRef = useRef(null);
  const inputRef = useRef(null);
  const selectedRef = useRef(null);
  const [currentText, setCurrentText] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const [hideBubbles, setHideBubbles] = useState(false);
  const [bubbleHideIndex, setBubbleHideIndex] = useState(0);
  const [selectedIndex, setSelectedIndex] = useState(0);

  const {
    lineBreakToHide,
    bubbles,
    setBubbles,
    dropdownOptions,
    notFoundText,
    inputLabel,
    hideInputLabel,
    showClearAll,
    className,
    onFocus,
    onBlur,
    passFocus,
    scrollIntoView,
    scrollAlignToTop
  } = props;

  const filteredDropdownOptions = dropdownOptions.filter(options =>
    options.label?.toLowerCase().includes(currentText.toLowerCase())
  );

  useEffect(() => {
    if (height !== bubbleRef.current?.offsetHeight) {
      const currentLine = Math.ceil((bubbleRef.current?.offsetHeight - 1) / oneLineHeight);
      if (currentLine === lineBreakToHide) {
        setHideBubbles(true);
        setBubbleHideIndex(bubbles.length);
      } else if (hideBubbles && currentLine < lineBreakToHide) {
        setHideBubbles(false);
      }
    }
    setHeight(bubbleRef?.current?.offsetHeight);
  }, [bubbles]);

  useEffect(() => {
    if (selectedRef.current && scrollIntoView) selectedRef.current.scrollIntoView(scrollAlignToTop);
  }, [selectedRef.current]);

  useEffect(() => {
    if (passFocus) {
      setInputToFocus();
      onFocus();
    } else {
      onBlur();
    }
  }, [passFocus]);

  function handleFocus() {
    setIsFocused(true);
    onFocus();
  }

  function handleBlur() {
    setIsFocused(false);
  }

  function handleChange(e) {
    setCurrentText(e.target.value);
  }

  function addBubble(value) {
    bubbles.push(value);
    setCurrentText('');
    setSelectedIndex(0);
    setBubbles([...bubbles]);
  }

  function handleKeyDown(e) {
    if (filteredDropdownOptions.length === 0) return;
    if (e.keyCode === KEY_CODES.ENTER && (currentText !== '' || filteredDropdownOptions.length !== 0)) {
      bubbles.push(filteredDropdownOptions[selectedIndex]);
      setCurrentText('');
      setSelectedIndex(0);
      setBubbles([...bubbles]);
    } else if (e.keyCode === KEY_CODES.BACK && (currentText === '' || e.target.selectionStart === 0)) {
      bubbles.pop();
      setBubbles([...bubbles]);
    } else if (e.keyCode === KEY_CODES.DOWN) {
      if (selectedIndex + 1 < filteredDropdownOptions.length) setSelectedIndex(selectedIndex + 1);
    } else if (e.keyCode === KEY_CODES.UP) {
      if (selectedIndex - 1 >= 0) setSelectedIndex(selectedIndex - 1);
    }
  }

  function deleteBubbleAtIndex(index) {
    bubbles.splice(index, 1);
    setBubbles([...bubbles]);
  }

  function clearSelections() {
    setBubbles([]);
  }

  function setInputToFocus() {
    setIsFocused(true);
    if (inputRef.current) inputRef.current.focus();
  }

  // Needed to appropriately keep focus on element
  function handleMouseDown(e) {
    e.preventDefault();
    setInputToFocus();
  }

  function handleMouseUp(e) {
    e.preventDefault();
  }

  const filteredBubbles = [...bubbles];
  const showMoreVisible = !isFocused && hideBubbles;

  if (showMoreVisible) filteredBubbles.splice(bubbleHideIndex - 1);

  return (
    <FormGroup
      className={classnames(className, 'bubble-input__form-container')}
      text={hideInputLabel ? undefined : inputLabel}
    >
      {bubbles.length ? (
        <i className="bubble-input__clear-all icon-cancel" title="Clear out selections" onClick={clearSelections} />
      ) : (
        undefined
      )}
      <div
        ref={bubbleRef}
        onClick={setInputToFocus}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        className={classnames('bubble-input__container pxl-form-control', {
          focused: isFocused,
          'clear-all-shown': showClearAll
        })}
      >
        {filteredBubbles.map((bubble, idx) => (
          <Bubble key={'bubble-' + idx} index={idx} onDeleteClick={deleteBubbleAtIndex} text={bubble.label} />
        ))}
        <div
          className={classnames('bubble-input__text-container', {
            'hide-input': showMoreVisible
          })}
        >
          <input
            ref={inputRef}
            className="clear-input-styling bubble-input__typed pxl-form-control"
            value={currentText}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
          />
          {isFocused && (
            <div className="bubble-input__dropdown">
              {filteredDropdownOptions.map((option, idx) => (
                <div
                  ref={selectedIndex === idx ? selectedRef : undefined}
                  key={'dropdown-bubble-option-' + idx}
                  onClick={() => {
                    addBubble(option);
                  }}
                  className={classnames('bubble-input__dropdown-item', { selected: selectedIndex === idx })}
                >
                  {option.label}
                </div>
              ))}
              {filteredDropdownOptions.length === 0 && (
                <div key="dropdown-bubble-option-none" className="bubble-input__dropdown-item">
                  {notFoundText}
                </div>
              )}
            </div>
          )}
        </div>
        {showMoreVisible && (
          <a className="bubble-input__show-more-text">Show {bubbles.length - filteredBubbles.length} more...</a>
        )}
      </div>
    </FormGroup>
  );
}

Bubble.propTypes = {
  text: string,
  index: number,
  onDeleteClick: func
};

function Bubble(props) {
  const { text, index, onDeleteClick } = props;

  function handleDelete() {
    onDeleteClick(index);
  }

  return (
    <div className="bubble-text__container">
      {text}
      <i onClick={handleDelete} className="bubble-text__delete icon-cancel" />
    </div>
  );
}
